<template>
  <div class="custom-video-player" :class="{ fullscreen: isFullscreen }">
    <div class="video-container" ref="videoContainer">
      <video
        ref="videoElement"
        :src="src"
        :poster="poster"
        :width="width"
        :height="height"
        :preload="preload"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @ended="onEnded"
        @click="togglePlay"
        @dblclick="toggleFullscreen"
      ></video>

      <!-- Center Play Button Overlay - only shows when video is not playing -->
      <div class="center-play-button" v-if="!isPlaying" @click="togglePlay">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64">
          <circle cx="12" cy="12" r="12" fill="rgba(0, 0, 0, 0.5)" />
          <path fill="white" d="M8 5v14l11-7z" />
        </svg>
      </div>

      <!-- Video Controls -->
      <div class="video-controls" v-show="!hideControls">
        <!-- Progress Bar -->
        <div class="progress-container" @click="seek">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${progress}%` }"></div>
            <div class="progress-handle" :style="{ left: `${progress}%` }"></div>
          </div>
          <div class="time-display">
            <span>{{ formatTime(currentTime) }}</span>
            <span>/</span>
            <span>{{ formatTime(duration) }}</span>
          </div>
        </div>

        <!-- Volume Control -->
        <div class="volume-container">
          <button class="control-button volume" @click="toggleVolumeControls">
            <span v-if="isMuted || volume === 0" class="volume-mute-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                />
              </svg>
            </span>
            <span v-else-if="volume < 0.5" class="volume-low-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3z" />
                <path
                  fill="currentColor"
                  d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"
                />
              </svg>
            </span>
            <span v-else class="volume-high-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                />
              </svg>
            </span>
          </button>
          <div class="volume-slider-container" v-show="showVolumeSlider" @click.stop>
            <div
              class="volume-slider-track"
              @mousedown="startVolumeAdjust"
              @touchstart.prevent="startVolumeAdjust"
            >
              <div class="volume-slider-fill" :style="{ height: `${volume * 100}%` }"></div>
              <div
                class="volume-slider-thumb"
                :style="{ bottom: `calc(${volume * 100}% - 10px)` }"
                @mousedown="startVolumeAdjust"
                @touchstart.prevent="startVolumeAdjust"
                @click.stop
              ></div>
            </div>
          </div>
        </div>

        <!-- Fullscreen Button -->
        <button class="control-button fullscreen" @click="toggleFullscreen">
          <span v-if="!isFullscreen" class="fullscreen-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path
                fill="currentColor"
                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
              />
            </svg>
          </span>
          <span v-else class="exit-fullscreen-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path
                fill="currentColor"
                d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

export default defineComponent({
  name: 'CustomVideoPlayer',
  props: {
    src: {
      type: String,
      required: true
    },
    poster: {
      type: String,
      default: ''
    },
    width: {
      type: [Number, String],
      default: '100%'
    },
    height: {
      type: [Number, String],
      default: 'auto'
    },
    preload: {
      type: String,
      default: 'metadata'
    },
    autoHideControls: {
      type: Boolean,
      default: true
    },
    autoHideDelay: {
      type: Number,
      default: 3000
    }
  },

  setup() {
    const videoElement = ref<HTMLVideoElement | null>(null)
    const videoContainer = ref<HTMLDivElement | null>(null)
    const isPlaying = ref(false)
    const isMuted = ref(false)
    const volume = ref<number>(1) // Start with full volume
    const currentTime = ref(0)
    const duration = ref(0)
    const isFullscreen = ref(false)
    const hideControls = ref(false)
    const showVolumeSlider = ref(false)
    const hideControlsTimeout = ref<number | null>(null)

    return {
      hideControlsTimeout,
      videoElement,
      videoContainer,
      isPlaying,
      isMuted,
      volume,
      currentTime,
      duration,
      isFullscreen,
      hideControls,
      showVolumeSlider
    }
  },
  computed: {
    progress() {
      if (this.duration === 0) return 0
      return (this.currentTime / this.duration) * 100
    }
  },
  emits: [
    'volume-change',
    'fullscreen-change',
    'play',
    'pause',
    'error',
    'mute-change',
    'unmute-change',
    'seek',
    'ended',
    'loaded-metadata',
    'time-update'
  ],
  watch: {
    autoHideControls(newValue) {
      if (!newValue) {
        this.hideControls = false
        if (this.hideControlsTimeout) {
          clearTimeout(this.hideControlsTimeout)
          this.hideControlsTimeout = null
        }
      } else {
        this.showControls()
      }
    }
  },

  unmounted() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('keydown', this.handleKeyDown)

    if (this.videoContainer) {
      this.videoContainer.removeEventListener('mousemove', this.handleMouseMove)
    }

    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout)
    }
  },
  mounted() {
    if (this.videoElement) {
      // Initialize volume to full (1)
      this.videoElement.volume = 1
      this.volume = 1

      // Add event listeners
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('keydown', this.handleKeyDown)

      if (this.videoContainer) {
        this.videoContainer.addEventListener('mousemove', this.handleMouseMove)
      }
    }
  },
  methods: {
    // Methods
    togglePlay() {
      if (!this.videoElement) return

      if (this.videoElement.paused) {
        this.videoElement
          .play()
          .then(() => {
            this.isPlaying = true
            this.$emit('play')
          })
          .catch(error => {
            console.error('Error playing video:', error)
            this.$emit('error', error)
          })
      } else {
        this.videoElement.pause()
        this.isPlaying = false
        this.$emit('pause')
      }

      this.showControls()
    },

    toggleMute() {
      if (!this.videoElement) return

      this.videoElement.muted = !this.videoElement.muted
      this.isMuted = this.videoElement.muted
      this.$emit('mute-change', this.isMuted)

      this.showControls()
    },

    toggleVolumeControls() {
      // First toggle mute if volume is 0
      if (this.volume === 0 || this.isMuted) {
        this.toggleMute()
      }

      // Then toggle the volume slider visibility
      this.showVolumeSlider = !this.showVolumeSlider

      // If we're showing the slider, add a document click handler to close it when clicking outside
      if (this.showVolumeSlider) {
        setTimeout(() => {
          document.addEventListener('click', this.closeVolumeSlider)
          document.addEventListener('touchstart', this.closeVolumeSlider)
        }, 10)
      }
    },

    closeVolumeSlider() {
      this.showVolumeSlider = false
      document.removeEventListener('click', this.closeVolumeSlider)
      document.removeEventListener('touchstart', this.closeVolumeSlider)
    },

    startVolumeAdjust(event: MouseEvent | TouchEvent) {
      event.preventDefault()
      event.stopPropagation()

      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        moveEvent.preventDefault()
        moveEvent.stopPropagation()

        const sliderTrack = document.querySelector('.volume-slider-track') as HTMLElement
        if (!sliderTrack) return

        const rect = sliderTrack.getBoundingClientRect()
        const trackTop = rect.top
        const trackHeight = rect.height

        // Get Y position based on event type
        let clientY: number
        if ('touches' in moveEvent) {
          clientY = moveEvent.touches[0].clientY
        } else {
          clientY = moveEvent.clientY
        }

        // Calculate position (0 at bottom, 1 at top)
        // Adjust to account for the thumb height (20px)
        let newVolume = 1 - (clientY - trackTop) / trackHeight

        // Clamp between 0 and 1
        newVolume = Math.max(0, Math.min(1, newVolume))

        // Update volume
        this.setVolume(newVolume)
      }

      const handleEnd = () => {
        document.removeEventListener('mousemove', handleMove as any)
        document.removeEventListener('touchmove', handleMove as any)
        document.removeEventListener('mouseup', handleEnd)
        document.removeEventListener('touchend', handleEnd)
      }

      document.addEventListener('mousemove', handleMove as any)
      document.addEventListener('touchmove', handleMove as any)
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchend', handleEnd)

      // Initial move to set volume based on first click/touch
      handleMove(event)
    },

    setVolume(newVolume: number) {
      if (!this.videoElement) return

      // Update the volume ref
      this.volume = newVolume

      // Apply to video element
      this.videoElement.volume = newVolume

      // Handle mute state
      if (newVolume === 0) {
        this.videoElement.muted = true
        this.isMuted = true
      } else if (this.isMuted) {
        this.videoElement.muted = false
        this.isMuted = false
      }

      this.$emit('volume-change', newVolume)
      this.showControls()
    },

    updateVolume() {
      if (!this.videoElement) return

      this.videoElement.volume = this.volume

      // If volume is set to 0, mute the video
      if (this.volume === 0) {
        this.videoElement.muted = true
        this.isMuted = true
      } else if (this.isMuted) {
        // If volume is increased from 0, unmute
        this.videoElement.muted = false
        this.isMuted = false
      }

      this.$emit('volume-change', this.volume)
      this.showControls()
    },

    seek(event: MouseEvent) {
      if (!this.videoElement) return

      const progressContainer = event.currentTarget as HTMLElement
      const rect = progressContainer.getBoundingClientRect()
      const pos = (event.clientX - rect.left) / rect.width

      this.videoElement.currentTime = pos * this.duration
      this.currentTime = this.videoElement.currentTime

      this.$emit('seek', this.currentTime)
      this.showControls()
    },

    toggleFullscreen() {
      if (!this.videoContainer) return

      if (!document.fullscreenElement) {
        this.videoContainer
          .requestFullscreen()
          .then(() => {
            this.isFullscreen = true
            this.$emit('fullscreen-change', true)
          })
          .catch(error => {
            console.error('Error attempting to enable fullscreen:', error)
          })
      } else {
        document
          .exitFullscreen()
          .then(() => {
            this.isFullscreen = false
            this.$emit('fullscreen-change', false)
          })
          .catch(error => {
            console.error('Error attempting to exit fullscreen:', error)
          })
      }

      this.showControls()
    },

    formatTime(seconds: number): string {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`
    },

    onTimeUpdate() {
      if (!this.videoElement) return

      this.currentTime = this.videoElement.currentTime
      this.$emit('time-update', this.currentTime)
    },

    onLoadedMetadata() {
      if (!this.videoElement) return

      this.duration = this.videoElement.duration
      this.$emit('loaded-metadata', {
        duration: this.duration,
        videoWidth: this.videoElement.videoWidth,
        videoHeight: this.videoElement.videoHeight
      })
    },

    onEnded() {
      this.isPlaying = false
      this.$emit('ended')
    },

    showControls() {
      this.hideControls = false

      if (this.autoHideControls && this.isPlaying) {
        if (this.hideControlsTimeout) {
          clearTimeout(this.hideControlsTimeout)
        }

        this.hideControlsTimeout = window.setTimeout(() => {
          this.hideControls = true
        }, this.autoHideDelay)
      }
    },
    // Event listeners for mouse movement
    handleMouseMove() {
      this.showControls()
    },

    // Fullscreen change event handler
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },

    // Keyboard shortcuts
    handleKeyDown(event: KeyboardEvent) {
      if (!this.videoElement) return

      switch (event.key.toLowerCase()) {
        case ' ':
        case 'k':
          this.togglePlay()
          event.preventDefault()
          break
        case 'm':
          this.toggleMute()
          event.preventDefault()
          break
        case 'Meta':
        case 'Escape':
        case 'f':
          this.toggleFullscreen()
          event.preventDefault()
          break
        case 'arrowleft':
          this.videoElement.currentTime = Math.max(0, this.videoElement.currentTime - 5)
          this.showControls()
          event.preventDefault()
          break
        case 'arrowright':
          this.videoElement.currentTime = Math.min(this.duration, this.videoElement.currentTime + 5)
          this.showControls()
          event.preventDefault()
          break
        case 'arrowup':
          this.volume = Math.min(1, this.volume + 0.1)
          this.updateVolume()
          event.preventDefault()
          break
        case 'arrowdown':
          this.volume = Math.max(0, this.volume - 0.1)
          this.updateVolume()
          event.preventDefault()
          break
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.custom-video-player {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #000;
  border-radius: var(--vff-border-radius, 4px);

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
  }

  .video-container {
    position: relative;
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .center-play-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 2;
      opacity: 0.9;
      transition: opacity 0.3s ease, transform 0.2s ease;

      &:hover {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.1);
      }

      svg {
        filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.5));
      }
    }

    .video-controls {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
      padding: 10px;
      display: flex;
      align-items: center;
      gap: 10px;
      transition: opacity 0.3s ease;

      .control-button {
        background: transparent;
        border: none;
        color: white;
        cursor: pointer;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;

        &:hover {
          opacity: 0.8;
        }

        svg {
          width: 24px;
          height: 24px;
        }
      }

      .progress-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        cursor: pointer;

        .progress-bar {
          position: relative;
          height: 5px;
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 2.5px;
          overflow: hidden;

          .progress-fill {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background-color: var(--dealer-primary-color, #f00);
            border-radius: 2.5px;
          }

          .progress-handle {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 12px;
            height: 12px;
            background-color: white;
            border-radius: 50%;
            opacity: 0;
            transition: opacity 0.2s ease;
          }

          &:hover .progress-handle {
            opacity: 1;
          }
        }

        .time-display {
          display: flex;
          gap: 4px;
          color: white;
          font-size: 12px;
        }
      }

      .volume-container {
        position: relative;
        display: flex;
        align-items: center;

        .volume-slider-container {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          box-sizing: border-box;
          width: 50px;
          background-color: rgba(0, 0, 0, 0.7);
          border-radius: 5px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 15px 0;
          z-index: 10;
          height: 120px;
          pointer-events: auto;
        }

        .volume-slider-track {
          position: relative;
          width: 6px;
          height: 90px;
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
          cursor: pointer;
        }

        .volume-slider-fill {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          background-color: white;
          border-radius: 3px;
        }

        .volume-slider-thumb {
          position: absolute;
          left: 50%;
          width: 20px;
          height: 20px;
          background-color: white;
          border-radius: 50%;
          transform: translateX(-50%);
          box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
          cursor: pointer;
          touch-action: none;
          margin-bottom: 0;
        }
      }
    }
  }
}

// Media queries for responsive design
@media (max-width: 768px) {
  .custom-video-player {
    .video-container {
      .video-controls {
        .volume-container {
          .volume-slider {
            width: 40px;
          }
        }

        .time-display {
          font-size: 10px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .custom-video-player {
    .video-container {
      .video-controls {
        padding: 8px;

        .control-button {
          width: 28px;
          height: 28px;

          svg {
            width: 20px;
            height: 20px;
          }
        }

        .volume-container {
          .volume-slider {
            display: none;
          }
        }
      }
    }
  }
}
</style>
